/**
 * API Gateway Service
 * Central entry point for all integration requests with rate limiting, authentication, and monitoring
 */

import { AuthenticationService } from './AuthenticationService';
import { RateLimitService } from './RateLimitService';
import { RequestLogger } from './RequestLogger';
import { ErrorHandler } from './ErrorHandler';

export interface APIRequest {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  url: string;
  headers: Record<string, string>;
  body?: any;
  timeout?: number;
  retryAttempts?: number;
}

export interface APIResponse {
  status: number;
  headers: Record<string, string>;
  data: any;
  duration: number;
  requestId: string;
  additional_data?: any; // For platform-specific additional data (e.g., Pipedrive pagination)
}

export interface APIGatewayConfig {
  defaultTimeout: number;
  maxRetryAttempts: number;
  rateLimitConfig: {
    windowMs: number;
    maxRequests: number;
  };
  enableLogging: boolean;
  enableMetrics: boolean;
}

export class APIGateway {
  private static instance: APIGateway;
  private authService: AuthenticationService;
  private rateLimitService: RateLimitService;
  private requestLogger: RequestLogger;
  private errorHandler: ErrorHandler;
  private config: APIGatewayConfig;

  private constructor(config: APIGatewayConfig) {
    this.config = config;
    this.authService = new AuthenticationService();
    this.rateLimitService = new RateLimitService(config.rateLimitConfig);
    this.requestLogger = new RequestLogger(config.enableLogging);
    this.errorHandler = new ErrorHandler();
  }

  static getInstance(config?: APIGatewayConfig): APIGateway {
    if (!APIGateway.instance) {
      const defaultConfig: APIGatewayConfig = {
        defaultTimeout: 30000,
        maxRetryAttempts: 3,
        rateLimitConfig: {
          windowMs: 60000, // 1 minute
          maxRequests: 100
        },
        enableLogging: true,
        enableMetrics: true
      };
      APIGateway.instance = new APIGateway(config || defaultConfig);
    }
    return APIGateway.instance;
  }

  /**
   * Process an API request through the gateway
   */
  async processRequest(request: APIRequest, clientId?: string): Promise<APIResponse> {
    const requestId = this.generateRequestId();
    const startTime = Date.now();

    try {
      // Log incoming request
      if (this.config.enableLogging) {
        await this.requestLogger.logRequest(requestId, request, clientId);
      }

      // Check rate limits
      if (clientId) {
        const rateLimitResult = await this.rateLimitService.checkRateLimit(clientId);
        if (!rateLimitResult.allowed) {
          throw new Error(`Rate limit exceeded. Try again in ${rateLimitResult.resetTime} seconds`);
        }
      }

      // Authenticate request if needed (credentials would be passed from integration config)
      const authenticatedRequest = await this.authService.authenticateRequest(request);

      // Execute request with retry logic
      const response = await this.executeWithRetry(authenticatedRequest, requestId);

      // Log successful response
      if (this.config.enableLogging) {
        await this.requestLogger.logResponse(requestId, response);
      }

      return {
        ...response,
        duration: Date.now() - startTime,
        requestId
      };

    } catch (error) {
      // Handle and log errors
      const handledError = await this.errorHandler.handleError(error, requestId);
      
      if (this.config.enableLogging) {
        await this.requestLogger.logError(requestId, handledError);
      }

      throw handledError;
    }
  }

  /**
   * Execute request with retry logic
   */
  private async executeWithRetry(request: APIRequest, requestId: string): Promise<APIResponse> {
    const maxAttempts = request.retryAttempts || this.config.maxRetryAttempts;
    let lastError: Error;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await this.executeRequest(request);
      } catch (error) {
        lastError = error as Error;
        
        // Check if error is retryable
        if (!this.errorHandler.isRetryableError(error as Error) || attempt === maxAttempts) {
          throw error;
        }

        // Calculate delay for next attempt
        const delay = this.calculateRetryDelay(attempt);
        await this.sleep(delay);

        // Log retry attempt
        if (this.config.enableLogging) {
          await this.requestLogger.logRetry(requestId, attempt, delay);
        }
      }
    }

    throw lastError!;
  }

  /**
   * Execute the actual HTTP request
   */
  private async executeRequest(request: APIRequest): Promise<APIResponse> {
    const timeout = request.timeout || this.config.defaultTimeout;
    
    // Create AbortController for timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(request.url, {
        method: request.method,
        headers: request.headers,
        body: request.body ? JSON.stringify(request.body) : undefined,
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const responseData = await response.json().catch(() => null);

      return {
        status: response.status,
        headers: this.headersToObject(response.headers),
        data: responseData,
        duration: 0, // Will be set by processRequest
        requestId: '' // Will be set by processRequest
      };

    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`Request timeout after ${timeout}ms`);
      }
      
      throw error;
    }
  }

  /**
   * Calculate exponential backoff delay
   */
  private calculateRetryDelay(attempt: number): number {
    const baseDelay = 1000; // 1 second
    const maxDelay = 30000; // 30 seconds
    const backoffMultiplier = 2;
    
    return Math.min(
      baseDelay * Math.pow(backoffMultiplier, attempt - 1),
      maxDelay
    );
  }

  /**
   * Sleep utility for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Convert Headers object to plain object
   */
  private headersToObject(headers: Headers): Record<string, string> {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  /**
   * HTTP GET convenience method
   */
  async get(url: string, options: { params?: Record<string, any>; headers?: Record<string, string> } = {}): Promise<APIResponse> {
    const queryString = options.params ? '?' + new URLSearchParams(options.params).toString() : '';
    const request: APIRequest = {
      method: 'GET',
      url: url + queryString,
      headers: options.headers || {}
    };
    return this.processRequest(request);
  }

  /**
   * HTTP POST convenience method
   */
  async post(url: string, data?: any, options: { params?: Record<string, any>; headers?: Record<string, string> } = {}): Promise<APIResponse> {
    const queryString = options.params ? '?' + new URLSearchParams(options.params).toString() : '';
    const request: APIRequest = {
      method: 'POST',
      url: url + queryString,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: data
    };
    return this.processRequest(request);
  }

  /**
   * HTTP PUT convenience method
   */
  async put(url: string, data?: any, options: { params?: Record<string, any>; headers?: Record<string, string> } = {}): Promise<APIResponse> {
    const queryString = options.params ? '?' + new URLSearchParams(options.params).toString() : '';
    const request: APIRequest = {
      method: 'PUT',
      url: url + queryString,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: data
    };
    return this.processRequest(request);
  }

  /**
   * HTTP DELETE convenience method
   */
  async delete(url: string, options: { params?: Record<string, any>; headers?: Record<string, string> } = {}): Promise<APIResponse> {
    const queryString = options.params ? '?' + new URLSearchParams(options.params).toString() : '';
    const request: APIRequest = {
      method: 'DELETE',
      url: url + queryString,
      headers: options.headers || {}
    };
    return this.processRequest(request);
  }

  /**
   * HTTP PATCH convenience method
   */
  async patch(url: string, data?: any, options: { params?: Record<string, any>; headers?: Record<string, string> } = {}): Promise<APIResponse> {
    const queryString = options.params ? '?' + new URLSearchParams(options.params).toString() : '';
    const request: APIRequest = {
      method: 'PATCH',
      url: url + queryString,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: data
    };
    return this.processRequest(request);
  }

  /**
   * Get gateway metrics
   */
  async getMetrics(): Promise<GatewayMetrics> {
    return {
      totalRequests: await this.requestLogger.getTotalRequests(),
      successRate: await this.requestLogger.getSuccessRate(),
      averageResponseTime: await this.requestLogger.getAverageResponseTime(),
      rateLimitHits: await this.rateLimitService.getRateLimitHits(),
      activeConnections: await this.getActiveConnections()
    };
  }

  private async getActiveConnections(): Promise<number> {
    // Implementation would track active connections
    return 0;
  }
}

export interface GatewayMetrics {
  totalRequests: number;
  successRate: number;
  averageResponseTime: number;
  rateLimitHits: number;
  activeConnections: number;
}

// Export singleton instance
export const apiGateway = APIGateway.getInstance();